import React, { useState, useEffect, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEMS_PER_PAGE = 20;

const CommercialSharesHistory = ({ isOpen, onClose }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filters
  const [searchName, setSearchName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, viewed, not_viewed

  useEffect(() => {
    if (isOpen) {
      fetchShares();
      setCurrentPage(1);
    }
  }, [isOpen]);

  const fetchShares = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API}/my-commercial-shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des partages');
      }
      
      const data = await response.json();
      setShares(data);
    } catch (err) {
      console.error('Error fetching shares:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtered shares
  const filteredShares = useMemo(() => {
    return shares.filter(share => {
      // Filter by status
      if (statusFilter === 'viewed' && !share.last_viewed_at) return false;
      if (statusFilter === 'not_viewed' && share.last_viewed_at) return false;
      
      // Filter by name/email
      if (searchName) {
        const search = searchName.toLowerCase();
        const nameMatch = share.commercial_name?.toLowerCase().includes(search);
        const emailMatch = share.commercial_email?.toLowerCase().includes(search);
        const studyMatch = share.study_name?.toLowerCase().includes(search);
        if (!nameMatch && !emailMatch && !studyMatch) return false;
      }
      
      // Filter by date
      if (dateFrom) {
        const shareDate = new Date(share.shared_at).setHours(0,0,0,0);
        const fromDate = new Date(dateFrom).setHours(0,0,0,0);
        if (shareDate < fromDate) return false;
      }
      
      if (dateTo) {
        const shareDate = new Date(share.shared_at).setHours(23,59,59,999);
        const toDate = new Date(dateTo).setHours(23,59,59,999);
        if (shareDate > toDate) return false;
      }
      
      return true;
    });
  }, [shares, searchName, dateFrom, dateTo, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredShares.length / ITEMS_PER_PAGE);
  const paginatedShares = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredShares.slice(start, end);
  }, [filteredShares, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, dateFrom, dateTo, statusFilter]);

  const handleDelete = async (analysisId, shareId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce partage ? Le commercial n\'aura plus accès à cette étude.')) {
      return;
    }
    
    try {
      setDeleting(shareId);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API}/commercial-share/${analysisId}/${shareId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }
      
      await fetchShares();
    } catch (err) {
      console.error('Error deleting share:', err);
      alert('Erreur lors de la suppression du partage');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setSearchName('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
  };

  if (!isOpen) return null;

  const viewedCount = shares.filter(s => s.last_viewed_at).length;
  const notViewedCount = shares.filter(s => !s.last_viewed_at).length;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '1.5rem',
              color: '#191d1e',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Partages aux Commerciaux
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.875rem' }}>
              {viewedCount} consulté(s) • {notViewedCount} non consulté(s)
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '0.5rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb'
        }}>
          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            {/* Search */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Rechercher par commercial / étude
              </label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Nom, email, étude..."
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Date from */}
            <div style={{ minWidth: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Du
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Date to */}
            <div style={{ minWidth: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Au
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Status filter */}
            <div style={{ minWidth: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box',
                  background: 'white'
                }}
              >
                <option value="all">Tous</option>
                <option value="viewed">Consultés</option>
                <option value="not_viewed">Non consultés</option>
              </select>
            </div>
            
            {/* Clear filters */}
            <button
              onClick={clearFilters}
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#6b7280',
                cursor: 'pointer'
              }}
            >
              Effacer filtres
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e5e7eb',
                borderTopColor: '#397DFF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }} />
              <p style={{ color: '#666' }}>Chargement...</p>
              <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
              `}</style>
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#dc2626'
            }}>
              <p>{error}</p>
              <button
                onClick={fetchShares}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  background: '#397DFF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Réessayer
              </button>
            </div>
          ) : filteredShares.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
              <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                {shares.length === 0 ? 'Aucun partage effectué' : 'Aucun résultat pour ces filtres'}
              </p>
              <p style={{ fontSize: '0.875rem' }}>
                {shares.length === 0 
                  ? 'Utilisez le bouton "Partager" sur une étude pour l\'envoyer à un commercial.'
                  : 'Modifiez vos critères de recherche ou effacez les filtres.'}
              </p>
            </div>
          ) : (
            <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {paginatedShares.map((share) => (
                <div
                  key={share.share_id}
                  style={{
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    padding: '1.25rem'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <h3 style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '1.125rem',
                        color: '#191d1e'
                      }}>
                        {share.study_name}
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ display: 'inline-flex' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#687175" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
                        <strong>{share.commercial_name || share.commercial_email}</strong>
                        {share.commercial_name && (
                          <span style={{ color: '#9ca3af' }}>({share.commercial_email})</span>
                        )}
                      </p>
                    </div>
                    
                    {/* Status badge */}
                    <div style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      background: share.last_viewed_at ? '#dcfce7' : '#fef3c7',
                      color: share.last_viewed_at ? '#166534' : '#92400e'
                    }}>
                      {share.last_viewed_at ? 'Consulté' : 'Non consulté'}
                    </div>
                  </div>

                  {/* Details */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    fontSize: '0.8125rem',
                    color: '#6b7280'
                  }}>
                    <div>
                      <span>Partagé le:</span>
                      <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                        {formatDate(share.shared_at)}
                      </span>
                    </div>
                    {share.last_viewed_at && (
                      <div>
                        <span>👁️ Consulté le:</span>
                        <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                          {formatDate(share.last_viewed_at)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '1rem'
                  }}>
                    <button
                      onClick={() => handleDelete(share.analysis_id, share.share_id)}
                      disabled={deleting === share.share_id}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        cursor: deleting === share.share_id ? 'not-allowed' : 'pointer',
                        opacity: deleting === share.share_id ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        marginLeft: 'auto'
                      }}
                    >
                      {deleting === share.share_id ? 'Suppression...' : 'Supprimer le partage'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === 1 ? '#f3f4f6' : 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1
                  }}
                >
                  ← Précédent
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: currentPage === page ? '#397DFF' : 'white',
                      color: currentPage === page ? 'white' : '#374151',
                      border: '1px solid',
                      borderColor: currentPage === page ? '#397DFF' : '#d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: currentPage === page ? '600' : '400',
                      minWidth: '40px'
                    }}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === totalPages ? '#f3f4f6' : 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1
                  }}
                >
                  Suivant →
                </button>
              </div>
            )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
            Page {currentPage}/{totalPages || 1} • {filteredShares.length} partage(s) sur {shares.length}
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#397DFF',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommercialSharesHistory;
